import { useState } from 'react';
import { ArrowRight, Check, Loader2, RefreshCw, UserPlus } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListClientsQueryKey,
  useCreateClient,
} from '@biasafe.queries/clients/clients';
import { useDeleteClient } from '@biasafe.queries/clients-client-id/clients-client-id';
import { toast } from 'sonner';
import type { ClientResponse } from '@biasafe.models';
import { SoteriaCard, soteriaCardCtaClass } from './SoteriaCard';
import type { ClientConfirmCard as ClientConfirmCardData } from '../hooks/useSoteriaChat';
import { IBKR_COUNTRIES } from '@/features/Clients/countries';
import { cn } from '@/lib/utils';

const TYPE_LABELS: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  HOUSEHOLD: 'Household',
  TRUST: 'Trust',
  CORPORATE: 'Corporate',
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate text-right font-medium text-foreground">
        {value}
      </span>
    </div>
  );
}

/**
 * ClientConfirmCard
 * -----------------
 * The Client Assistant proposes a new prospect; this card lets the advisor
 * review the details and click Create. The agent never writes — Create fires
 * the SAME POST /clients the "New Client" form uses (the confirm step is an
 * explicit human action). On success the card flips to a "created" state with a
 * link to the new client, and the directory list is refreshed.
 *
 * REPLACE variant (`kind === "confirm_replace"`): the platform has no
 * edit-client API, so "changing" a prospect means replacing it. When an
 * existing prospect (`replaces_id`) shares the email, confirming chains
 * DELETE /clients/{replaces_id} → POST /clients with the new details. If the
 * delete fails (e.g. the prospect gained an account since the proposal), we
 * stop before creating so we never leave a duplicate behind.
 */
export function ClientConfirmCard({ client }: { client: ClientConfirmCardData }) {
  const create = useCreateClient();
  const del = useDeleteClient();
  const queryClient = useQueryClient();
  const [createdId, setCreatedId] = useState<string | null>(null);

  const isReplace = client.kind === 'confirm_replace' && !!client.replaces_id;

  const countryName =
    IBKR_COUNTRIES.find((c) => c.code === client.residential_country)?.name ??
    client.residential_country;
  const typeLabel = TYPE_LABELS[client.client_type] ?? client.client_type;

  const pending = del.isPending || create.isPending;

  const runCreate = () => {
    create.mutate(
      {
        data: {
          displayName: client.display_name,
          firstName: client.first_name,
          lastName: client.last_name,
          clientType: client.client_type,
          email: client.email,
          phone: client.phone,
          residentialCountry: client.residential_country,
        },
      },
      {
        onSuccess: (res: ClientResponse) => {
          toast.success(
            isReplace
              ? `${client.display_name} replaced.`
              : `${client.display_name} added.`,
          );
          void queryClient.invalidateQueries({
            queryKey: getListClientsQueryKey(),
          });
          setCreatedId(res.id);
        },
        onError: () => toast.error('Could not create the client.'),
      },
    );
  };

  const handleConfirm = () => {
    // Replace = delete the old prospect first, then create. Only chain the
    // create on a successful delete so a 409 (the prospect gained an account or
    // signed paperwork since the proposal) never leaves two records behind.
    if (isReplace) {
      del.mutate(
        { clientId: client.replaces_id! },
        {
          onSuccess: () => runCreate(),
          onError: () =>
            toast.error(
              'Could not replace the existing prospect: it may now have an account or signed documents. Update it in the client directory instead.',
            ),
        },
      );
      return;
    }
    runCreate();
  };

  if (createdId) {
    return (
      <SoteriaCard
        variant="success"
        icon={<Check className="h-4 w-4" />}
        eyebrow={isReplace ? 'Client replaced' : 'Client created'}
        title={client.display_name}
        subtitle={isReplace ? 'Re-added as a prospect.' : 'Added as a prospect.'}
        cta={
          <Link
            to="/clients/$clientId"
            params={{ clientId: createdId }}
            className={soteriaCardCtaClass('success')}
          >
            Open
            <ArrowRight className="h-3 w-3" />
          </Link>
        }
      />
    );
  }

  return (
    <SoteriaCard
      variant="default"
      icon={
        isReplace ? (
          <RefreshCw className="h-4 w-4" />
        ) : (
          <UserPlus className="h-4 w-4" />
        )
      }
      eyebrow={
        isReplace
          ? 'Replace prospect: review & confirm'
          : 'New client: review & confirm'
      }
      title={client.display_name}
    >
      {isReplace && (
        <p className="mt-2 text-xs text-muted-foreground">
          Deletes the existing prospect
          {client.replaces_reference_code
            ? ` (${client.replaces_reference_code})`
            : ''}{' '}
          and re-adds it with the details below.
        </p>
      )}

      <div className="mt-3 space-y-1.5 rounded-lg bg-muted/50 p-3">
        <Row label="Email" value={client.email} />
        <Row label="Phone" value={client.phone} />
        <Row label="Country of residence" value={countryName} />
        <Row label="Type" value={typeLabel} />
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={pending}
          className={cn(
            soteriaCardCtaClass('default'),
            'gap-1.5 disabled:opacity-60',
          )}
        >
          {pending && <Loader2 className="h-3 w-3 animate-spin" />}
          {isReplace
            ? pending
              ? 'Replacing…'
              : 'Replace client'
            : pending
              ? 'Creating…'
              : 'Create client'}
        </button>
      </div>
    </SoteriaCard>
  );
}
